import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { AttendanceEventSourceBadge } from './AttendanceEventSourceBadge';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import type { AttendanceEvent } from '../types/attendance';
import {
  attendanceSourceAccessibility,
  resolveAttendanceSourceDisplay,
} from '../utils/attendanceEventSource';
import { breakSegmentsFromEvents } from '../utils/checkoutBreak';
import {
  formatAttendanceTimestamp,
  formatDurationMinutes,
} from '../utils/dateTime';

type Props = {
  events: AttendanceEvent[];
};

function breakDurationByEndKey(
  events: AttendanceEvent[],
): Map<string, number> {
  const durations = new Map<string, number>();
  for (const segment of breakSegmentsFromEvents(events)) {
    if (segment.isOpen || segment.durationMinutes == null || !segment.endAt) {
      continue;
    }
    durations.set(segment.endAt, segment.durationMinutes);
  }
  return durations;
}

export function AttendanceEventTimeline({ events }: Props) {
  const palette = useAppColors();
  const breakDurations = breakDurationByEndKey(events);

  if (!events.length) {
    return (
      <Text
        style={[styles.empty, { color: palette.textSecondary }]}
        accessibilityLabel="No attendance events"
      >
        No attendance events were recorded for this day.
      </Text>
    );
  }

  return (
    <View accessibilityRole="summary" style={styles.list}>
      {events.map((event, index) => {
        const time = formatAttendanceTimestamp(
          event.localTimestamp,
          event.timestamp,
        );
        const sourceDisplay = resolveAttendanceSourceDisplay(event);
        const sourceCaption = attendanceSourceAccessibility(sourceDisplay);
        const endKey = event.localTimestamp ?? event.timestamp ?? '';
        const breakMinutes =
          event.eventType?.toUpperCase() === 'BREAK_IN'
            ? breakDurations.get(endKey) ?? null
            : null;
        const breakDurationLabel =
          breakMinutes != null
            ? `Duration: ${formatDurationMinutes(breakMinutes)}`
            : null;
        const parts = [
          event.eventLabel,
          time,
          breakDurationLabel,
          event.locationName ? `at ${event.locationName}` : null,
          sourceCaption ? `via ${sourceCaption}` : null,
        ].filter(Boolean);

        return (
          <View
            key={String(event.eventId ?? `${event.eventType}-${index}`)}
            style={styles.row}
            accessible
            accessibilityLabel={parts.join(', ')}
          >
            <View style={styles.rail}>
              <View
                style={[styles.dot, { backgroundColor: palette.primary }]}
              />
              {index < events.length - 1 ? (
                <View
                  style={[styles.line, { backgroundColor: palette.border }]}
                />
              ) : null}
            </View>
            <View style={styles.content}>
              <Text
                style={[styles.label, { color: palette.textPrimary }]}
              >
                {event.eventLabel}
              </Text>
              <View style={styles.timeRow}>
                <Text style={[styles.meta, { color: palette.textSecondary }]}>
                  {time}
                </Text>
                <AttendanceEventSourceBadge
                  punch={event}
                  editedTime={event.localTimestamp ?? event.timestamp}
                />
              </View>
              {breakDurationLabel ? (
                <Text style={[styles.meta, { color: palette.textSecondary }]}>
                  {breakDurationLabel}
                </Text>
              ) : null}
              {event.locationName ? (
                <Text style={[styles.meta, { color: palette.textSecondary }]}>
                  Location: {event.locationName}
                </Text>
              ) : null}
              {event.faceVerified === true ? (
                <Text style={[styles.verified, { color: palette.success }]}>
                  Verified using face attendance
                </Text>
              ) : null}
            </View>
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  list: { gap: spacing.sm },
  empty: { ...typography.body },
  row: { flexDirection: 'row', gap: spacing.md, minHeight: 56 },
  rail: { width: 16, alignItems: 'center' },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginTop: 4,
  },
  line: {
    flex: 1,
    width: 2,
    marginTop: 4,
  },
  content: { flex: 1, gap: 2, paddingBottom: spacing.md },
  timeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
  },
  label: { ...typography.body, fontWeight: '600' },
  meta: { ...typography.caption },
  verified: { ...typography.caption },
});
